import { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { DossierDock, DOSSIER_IMAGES } from '../components/science/DossierDock';
import { FlowBackground } from '../components/home/FlowBackground';
import { GLASS } from '../components/home/glass';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { ExternalLink, BookOpen, Activity, Scale, Leaf, Ruler, Microscope, Package } from 'lucide-react';
import {
    SCIENCE_INTRO,
    SCIENCE_DOSSIERS,
    SCIENCE_SOURCES,
} from '../data/scienceContent';

// Purely educational content. No buy CTAs. Preserves yellow NVWA disclaimer.
const pick = (v, lang) => (typeof v === 'string' ? v : v[lang]);

const SECTION_ICONS = {
    why: BookOpen,
    mechanism: Activity,
    dose: Scale,
    material: Leaf,
    standard: Ruler,
    studies: Microscope,
    carrier: Package,
};

// Renders **bold** and [n] / [n,m] citation markers as superscript links to the sources list.
function RichText({ text }) {
    const parts = text.split(/(\*\*[^*]+\*\*|\[\d+(?:,\s*\d+)*\])/g).filter(Boolean);
    return (
        <>
            {parts.map((part, i) => {
                if (part.startsWith('**')) {
                    return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
                }
                if (part.startsWith('[')) {
                    const nums = part.slice(1, -1).split(',').map(s => s.trim());
                    return (
                        <sup key={i} className="ml-0.5 text-[0.7em] font-semibold text-primary leading-none">
                            {nums.map((n, j) => (
                                <span key={n}>
                                    <a href={`#ref-${n}`} className="hover:underline" data-testid={`cite-${n}`}>{n}</a>
                                    {j < nums.length - 1 && ','}
                                </span>
                            ))}
                        </sup>
                    );
                }
                return <span key={i}>{part}</span>;
            })}
        </>
    );
}

function DossierSection({ dossier }) {
    const { lang } = useLanguage();
    const verdictLabel = lang === 'nl' ? 'Onze afweging' : 'Our assessment';

    return (
        <motion.article
            id={dossier.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="mb-24 scroll-mt-32"
            data-testid={`dossier-${dossier.id}`}
        >
            <div className="mb-10 pb-6 border-b border-border">
                <div className="flex items-baseline gap-4 sm:gap-6">
                    <span className="font-condensed text-5xl sm:text-6xl font-bold text-secondary-dark/25 leading-none shrink-0">
                        {dossier.num}
                    </span>
                    <div className="min-w-0">
                        <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-foreground leading-tight break-words hyphens-manual">
                            {dossier.name[lang]}
                        </h2>
                        <p className="mt-2 font-condensed font-bold text-lg text-primary leading-none" data-testid={`dossier-dose-${dossier.id}`}>
                            {dossier.dose[lang]}
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-10">
                {dossier.sections.map((s, i) => {
                    const Icon = SECTION_ICONS[s.icon] || BookOpen;
                    return (
                    <div key={i}>
                        <h3 className="font-heading text-sm uppercase tracking-widest text-foreground mb-4 flex items-center gap-3">
                            <Icon size={16} className="text-primary shrink-0" />
                            {s.title[lang]}
                        </h3>
                        <div className="space-y-4">
                            {s.paras[lang].map((p, j) => (
                                <p key={j} className="text-base text-slate-800 leading-relaxed">
                                    <RichText text={p} />
                                </p>
                            ))}
                        </div>
                    </div>
                    );
                })}
            </div>

            <div className={`mt-10 p-6 sm:p-7 ${GLASS}`} data-testid={`dossier-verdict-${dossier.id}`}>
                <h4 className="text-xs uppercase tracking-widest font-bold text-secondary-dark mb-3">{verdictLabel}</h4>
                <div className="space-y-3">
                    {dossier.verdict[lang].map((p, i) => (
                        <p key={i} className="text-sm text-slate-700 leading-relaxed">
                            <RichText text={p} />
                        </p>
                    ))}
                </div>
            </div>
        </motion.article>
    );
}

export default function Science() {
    const { lang } = useLanguage();
    const sourcesLabel = lang === 'nl' ? 'Bronnen' : 'Sources';
    const dossiersRef = useRef(null);

    return (
        <div>
            <SEO
                title="Science & Research | Sleepstack"
                description="How Sleepstack goes from published human research to an actual formula: ingredients, doses, raw materials and the trade-offs behind each choice."
                path="/science"
            />
            <PreSaleBanner />
            <Navbar />
            <main
                className="relative isolate"
                style={{ paddingTop: 'var(--page-offset, 128px)', background: 'linear-gradient(to bottom, #FFFFFF 0%, #FDF6F9 40%, #FCF3F6 75%, #FFFFFF 100%)' }}
            >
                <FlowBackground variant="science" />
                {/* Editorial hero */}
                <section className="pt-12 pb-8">
                    <div className="section-container max-w-3xl">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                                <span className="w-8 h-px bg-primary" />
                                {SCIENCE_INTRO.eyebrow[lang]}
                            </div>
                            <div className={`p-6 sm:p-8 lg:p-10 ${GLASS}`} data-testid="science-hero-panel">
                            <h1
                                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-foreground leading-[1.02] mb-6"
                                data-testid="science-page-title"
                            >
                                {SCIENCE_INTRO.title[lang]}
                            </h1>
                            <div className="space-y-4 max-w-2xl">
                                {SCIENCE_INTRO.paras[lang].map((p, i) => (
                                    <p key={i} className="text-lg md:text-xl text-muted-foreground leading-relaxed">{p}</p>
                                ))}
                            </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Mandatory NVWA/EFSA disclaimer, kept yellow */}
                <section className="pt-4">
                    <div className="section-container max-w-3xl">
                        <div className={`p-5 ${GLASS} !bg-amber-50/50 !border-amber-200/80`} data-testid="science-disclaimer">
                            <p className="text-xs text-amber-800 leading-relaxed">
                                {lang === 'nl'
                                    ? 'Op deze pagina leggen we uit welk onderzoek we gebruiken en hoe we onze ingrediënten en doseringen kiezen. De beschreven resultaten horen bij de onderzochte stoffen, hoeveelheden en deelnemers. Ze mogen niet worden gelezen als gezondheidsclaims over Sleepstack of als bewijs dat onze volledige formule dezelfde effecten heeft.'
                                    : 'On this page we explain which research we use and how we choose our ingredients and dosages. The results described apply to the specific substances, amounts and participants studied. They should not be read as health claims about Sleepstack or as proof that our complete formula has the same effects.'}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Dossiers, sticky side navigation on desktop */}
                <section className="py-16">
                    <div className="section-container max-w-5xl lg:grid lg:grid-cols-[250px_minmax(0,1fr)] lg:gap-12">
                        <aside className="hidden lg:block" data-testid="science-side-nav">
                            <nav className={`sticky top-32 space-y-1 p-3 ${GLASS}`}>
                                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3 px-3">
                                    Dossiers
                                </div>
                                {SCIENCE_DOSSIERS.map(d => (
                                    <a
                                        key={d.id}
                                        href={`#${d.id}`}
                                        className="flex items-center gap-3 px-2 py-1.5 rounded-xl text-sm text-slate-600 hover:text-primary hover:bg-white/50 transition-colors"
                                        data-testid={`side-nav-${d.id}`}
                                    >
                                        <span className="w-10 h-10 rounded-full bg-white/70 border border-white/80 flex items-center justify-center shrink-0">
                                            <img src={DOSSIER_IMAGES[d.id]} alt="" className="w-8 h-8 object-contain" data-testid={`side-nav-icon-${d.id}`} />
                                        </span>
                                        <span className="leading-tight break-words hyphens-manual min-w-0">{d.name[lang]}</span>
                                    </a>
                                ))}
                                <div className="pt-2 mt-2 border-t border-white/70">
                                    <a href="#sources" className="flex px-3 py-2 rounded-xl text-sm text-slate-600 hover:text-primary hover:bg-white/50 transition-colors" data-testid="side-nav-sources">
                                        {sourcesLabel}
                                    </a>
                                </div>
                            </nav>
                        </aside>
                        <div className="max-w-3xl min-w-0 pb-16 lg:pb-0" ref={dossiersRef}>
                            {SCIENCE_DOSSIERS.map(d => (
                                <DossierSection key={d.id} dossier={d} />
                            ))}
                        </div>
                    </div>
                    <DossierDock dossiers={SCIENCE_DOSSIERS} containerRef={dossiersRef} />
                </section>

                {/* Sources */}
                <section id="sources" className="py-16 scroll-mt-32" data-testid="science-sources">
                    <div className="section-container max-w-3xl">
                        <h2 className="font-heading text-sm uppercase tracking-widest text-foreground mb-8 flex items-center gap-3">
                            <span className="w-5 h-px bg-primary" />
                            {sourcesLabel}
                        </h2>
                        <ol className="space-y-4">
                            {SCIENCE_SOURCES.map(src => (
                                <li key={src.n} id={`ref-${src.n}`} className="flex gap-4 scroll-mt-32 text-sm leading-relaxed" data-testid={`source-${src.n}`}>
                                    <span className="font-condensed font-bold text-primary w-6 shrink-0 text-right">{src.n}</span>
                                    <div className="min-w-0">
                                        <a
                                            href={src.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-slate-800 hover:text-primary transition-colors inline"
                                        >
                                            {pick(src.ref, lang)}
                                            <ExternalLink size={11} className="inline ml-1.5 -mt-0.5 text-muted-foreground" />
                                        </a>
                                        {src.note && (
                                            <span className="block text-xs text-muted-foreground mt-0.5">{src.note[lang]}</span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
